import { apiFetch } from "@/lib/apiClient";
import { mockAwareFetch } from "@/portals/physician/api/transport";

export type UploadJobStatus = "processing" | "complete" | "failed" | "cancelled";

/** A record that could not be saved — either a recognized resource type missing a
 *  required field, or one whose data crashed during mapping/validation. Neither the
 *  system nor the patient can fix these, so they're surfaced as a transparency log
 *  (layered into ZipContentsModal) rather than something to action. */
export type ParsingIssue = {
  resource_type: string;
  source_file: string | null;
  error_type: string;
  reason: string;
  /** Deterministic, content-derived identifier for the specific record this issue is
   *  about — lets the frontend tell apart multiple issues that share the same
   *  resource_type + source_file (e.g. three separate Observations from one C-CDA
   *  file) instead of rendering indistinguishable duplicate cards. Only guaranteed to
   *  match an actually-saved row when error_type is "needs_review"; for other error
   *  types the record itself was never stored, so this identifies content only. Can
   *  be absent on older upload history recorded before this field existed. */
  record_id?: string | null;
};

export type UploadResponse = {
  upload_id: string;
  status: UploadJobStatus;
  file_name: string;
  uploaded_at: string;
  /** Populated only for a zip upload — the backend already scanned its contents for
   *  unsupported files synchronously before this response came back (see
   *  upload_service.py's _find_unsupported_zip_entries), so this is known immediately,
   *  well before the worker finishes. null for every other upload path. */
  parsing_issues: ParsingIssue[] | null;
};

export type ZipContentFile = {
  name: string;
  size: number;
  compressed_size: number;
};

/** Thrown by getUploadStatus/listUploads with the real HTTP status attached, so callers
 *  can tell a permanent 404 (id doesn't exist / isn't yours) apart from a transient failure. */
export class UploadStatusError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Thrown by uploadMedicalZip with whether this was a real rejection (the
 *  server answered — validation failure, unsupported vendor, etc. — so the
 *  upload definitely never happened) or a network-level failure (timeout,
 *  dropped connection, aborted request — the server may have received and
 *  accepted the upload anyway; this client just never found out). Callers
 *  need that distinction to decide whether to show the failure immediately
 *  or try to recover the real upload id first — see
 *  UploadHistoryContext.recoverUploadId. */
export class UploadRequestError extends Error {
  networkError: boolean;
  constructor(message: string, networkError: boolean) {
    super(message);
    this.networkError = networkError;
  }
}

export type UploadStatusResponse = {
  upload_id: string;
  status: UploadJobStatus;
  file_name: string;
  // Patient-entered label from the Upload Records modal's "Title" field —
  // null wherever not given; callers fall back to file_name, same
  // convention as imaging_studies.title.
  title: string | null;
  uploaded_at: string;
  file_count: number | null;
  error_message: string | null;
  files: ZipContentFile[] | null;
  parsing_issues: ParsingIssue[] | null;
  // TEMP-TIMING: seconds from upload accepted to the worker fully finishing
  // (see the backend's UploadJob.processed_at) — null until then. Diagnostic
  // only; grep TEMP-TIMING across both repos to remove this feature later.
  processing_seconds: number | null;
  // Both null unless status is "processing" — see the backend's
  // get_upload_processing_progress for what each stage means and how
  // percent is computed (a real fraction of actual page counts where one is
  // known, never a synthetic/timed guess).
  processing_stage: "queued" | "extracting" | "converting" | "processing" | null;
  processing_progress: number | null;
};

/**
 * Every function below accepts an optional `actingAsProfileId` — pass a
 * Dedicated Profile's id to upload/view/list records *for that profile*
 * instead of the caller's own. Omit it (or pass undefined) for "myself";
 * that's also exactly what happens if you pass your own id. The backend
 * re-checks authorization on every single call (see
 * app/dependencies.py's get_effective_patient_id) — this header carries no
 * trust on its own, it's just which profile you're asking to act on.
 */
function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

/** Uploads an Epic/Athena export .zip for async processing. Do not set Content-Type — the browser sets the multipart boundary.
 *  Pass `signal` to let the caller abort the request client-side (e.g. the patient hits Cancel before the request finishes).
 *  `clientRequestId` — a fresh crypto.randomUUID() generated by the caller before this call, stored on the server's
 *  UploadJob row — lets the caller recover the real upload_id later (getUploadStatusByClientId) if THIS response is
 *  lost to a dropped connection/tunnel reset/timeout, without guessing from the file name and a timestamp. Optional:
 *  omitting it just means recovery isn't possible for that particular upload. */
export async function uploadMedicalZip(file: File, actingAsProfileId?: string, signal?: AbortSignal, clientRequestId?: string, title?: string): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  if (clientRequestId) formData.append("client_request_id", clientRequestId);
  if (title && title.trim()) formData.append("title", title.trim());

  const { data, error, networkError } = await apiFetch<UploadResponse>("/api/v1/data-pipeline/upload", {
    method: "POST",
    body: formData,
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
    signal,
    // The default 30s is wrong for an upload: this response only comes back
    // AFTER the backend has streamed the whole file in, scored/unpacked the
    // zip, pushed it to S3, and committed the row — a multi-MB file over a
    // slow tunnel routinely takes longer than that, and hitting the timeout
    // makes apiFetch report networkError, which sends UploadRecords into
    // recover-by-client-id mode against a row the backend hasn't written
    // yet (the 404 loop). Ignored when the caller passes its own `signal`.
    timeoutMs: 5 * 60_000,
  });

  if (error) {
    throw new UploadRequestError(error.message || "Upload failed", networkError);
  }

  return data as UploadResponse;
}

/** Looks up an upload by the clientRequestId passed to uploadMedicalZip, instead of its upload_id — for when that
 *  call's own response was lost and the real upload_id was never learned. Throws UploadStatusError(404, ...) via
 *  the same path as getUploadStatus if no upload with this token exists (never created, or belongs to someone
 *  else) — indistinguishable from the caller's point of view, exactly like an unknown upload_id would be. */
export async function getUploadStatusByClientId(clientRequestId: string, actingAsProfileId?: string): Promise<UploadStatusResponse> {
  const { data, error, status } = await apiFetch<UploadStatusResponse>(
    `/api/v1/data-pipeline/upload/by-client-id/${clientRequestId}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new UploadStatusError(status, error.message || "Failed to fetch upload status");
  }

  return data as UploadStatusResponse;
}

/** Polls the live status of a previously-accepted upload while it's parsed asynchronously by the worker. */
export async function getUploadStatus(uploadId: string, actingAsProfileId?: string): Promise<UploadStatusResponse> {
  const { data, error, status } = await apiFetch<UploadStatusResponse>(
    `/api/v1/data-pipeline/upload/${uploadId}/status`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new UploadStatusError(status, error.message || "Failed to fetch upload status");
  }

  return data as UploadStatusResponse;
}

/** Default/max page size for listUploads — mirrors the backend's own
 *  _UPLOADS_DEFAULT_LIMIT/_UPLOADS_MAX_LIMIT (upload_router.py). "Your
 *  Uploads" is a recent-activity list, not a records table, so 20 covers
 *  several screens of scroll per fetch without over-fetching. */
export const UPLOADS_PAGE_SIZE = 20;

export type UploadListResponse = {
  success: boolean;
  data: UploadStatusResponse[];
  count: number;
  hasMore: boolean;
};

/**
 * One page of upload history for the authenticated patient (or the acting-as
 * profile), newest first. Paginated (limit/offset) — the backend returns a
 * patient's entire upload history unbounded otherwise, which doesn't scale
 * as history grows. Pass `offset` to fetch the next page; `hasMore` in the
 * response tells the caller whether another page exists past this one.
 */
export async function listUploads(
  actingAsProfileId?: string,
  options?: { limit?: number; offset?: number },
): Promise<UploadListResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(options?.limit ?? UPLOADS_PAGE_SIZE));
  params.set("offset", String(options?.offset ?? 0));

  const { data, error } = await apiFetch<UploadListResponse>(
    `/api/v1/data-pipeline/uploads?${params.toString()}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) }
  );

  if (error) {
    throw new Error(error.message || "Failed to fetch upload history");
  }

  // A 2xx response whose body failed to parse (apiFetch's `data: null` with
  // no `error`) is a real failure, not "this patient genuinely has zero
  // uploads" — silently defaulting to empty here would blank out an
  // already-populated history on a transient hiccup instead of surfacing it.
  if (!data) {
    throw new Error("Received an empty or invalid response. Please try again.");
  }

  return data;
}

/** One parsed clinical record — shape is shared across every resource type; only
 *  `fields` varies (medication_name/dose_quantity for a MedicationRequest,
 *  encounter_class/start_date for an Encounter, etc). */
export type ParsedRecord = {
  id: string;
  resourceType: string;
  sourceVendor: string;
  uploadId: string | null;
  confidenceScore: number;
  needsReview: boolean;
  reviewReason: string | null;
  createdAt: string;
  updatedAt: string;
  fields: Record<string, unknown>;
  /** Module 2: MedicationRequest only. Non-null while the record is part of an
   *  open review item; records sharing a reviewItemId are one conflict. Absent
   *  on other resource types and on backends that predate Module 2. */
  review?: RecordReview | null;
  /** Module 2: how the record's current value was settled. null while under review. */
  origin?: RecordOrigin | null;
  /** Module 5: what a physician's decision did to this record (origin stays PHYSICIAN_VERIFIED). */
  reviewOutcome?: RecordReviewOutcome | null;
  /** Module 5: when reviewOutcome.effect is CORRECTED, which field keys the physician corrected
   *  (their corrected values are already applied in `fields`). */
  correctedFields?: string[];
  /** Combined view (scope=all) only: the same record was found in several
   *  uploads/syncs and is shown once — `sources` lists every copy, this one first. */
  duplicates?: RecordDuplicates | null;
  /** MedicationRequest: confidently matched to a standard RxNorm medicine. */
  rxnormVerified?: boolean;
  /** MedicationRequest: resolved history, stored before medication review
   *  existed — never raises an issue; shown under "Earlier records". */
  reviewBaseline?: boolean;
};

export type RecordSource = {
  recordId: string;
  uploadId: string | null;
  sourceVendor: string;
  /** The uploaded file's name, or e.g. "Epic sync". */
  sourceLabel: string | null;
  createdAt: string;
};

export type RecordDuplicates = {
  count: number;
  sources: RecordSource[];
};

export type RecordEffect = "KEPT" | "DISMISSED" | "DISCONTINUED" | "CORRECTED";

export type RecordReviewOutcome = {
  effect: RecordEffect;
  decidedAt: string;
  reviewItemId: string;
};

export type ReviewTriggerType =
  | "VENDOR_DISAGREEMENT"
  | "MISSING_INFORMATION"
  | "LOW_CONFIDENCE_EXTRACTION"
  | "UNRECOGNIZED_NAME"
  | "RXNORM_REVIEW_BAND";

/** Where an open review item stands (Module 3). */
export type OpenReviewItemStatus = "AWAITING_PATIENT" | "AWAITING_PHYSICIAN" | "IN_PHYSICIAN_REVIEW" | "NEEDS_MORE_INFO";

export type RecordReview = {
  status: "UNDER_REVIEW";
  reviewItemId: string;
  recordRole: "EXISTING" | "INCOMING" | "SUBJECT";
  triggerType: ReviewTriggerType;
  /** Module 3. Optional only so records from a backend that predates it still type-check. */
  itemStatus?: OpenReviewItemStatus;
  /** The patient may answer now — waiting on them, or already with a
   *  physician but never answered (a late answer). */
  canRespond?: boolean;
};

export type RecordOrigin = "SYSTEM_RESOLVED" | "PATIENT_REPORTED" | "PHYSICIAN_VERIFIED";

export type ParsedRecordsResponse = {
  success: boolean;
  data: ParsedRecord[];
  count: number;
  hasMore?: boolean;
  sourceVendors: string[];
  sourceGroups: SourceGroup[];
  mixedSourceGroups: boolean;
};

export type SourceGroup = "structured" | "pdf";

export type ParsedRecordsDetail = {
  records: ParsedRecord[];
  count: number;
  hasMore: boolean;
  sourceVendors: string[];
  sourceGroups: SourceGroup[];
  mixedSourceGroups: boolean;
};

/** Parsed records for the authenticated patient (or the acting-as profile) — GET
 *  /api/patient/records. Pass uploadId to scope to a single upload (what "View Data"
 *  uses); pass resourceTypes to narrow which FHIR resource types come back. Omit
 *  both for everything ever parsed. */
const COMBINED_RECORDS_TIMEOUT_MS = 120_000;

export async function getPatientRecords(options?: {
  uploadId?: string;
  resourceTypes?: string[];
  actingAsProfileId?: string;
}): Promise<ParsedRecord[]> {
  const params = new URLSearchParams();
  if (options?.uploadId) {
    params.set("upload_id", options.uploadId);
  } else {
    // The patient's clinical pages show ALL their completed uploads and syncs
    // together, with the same record from several of them merged into one row
    // ("Found in N records") — not just the latest upload. The whole history
    // comes back in one response (duplicates are merged across all of it).
    params.set("scope", "all");
  }
  if (options?.resourceTypes?.length) params.set("resource_types", options.resourceTypes.join(","));
  const query = params.toString();

  // mockAwareFetch is plain apiFetch unless VITE_USE_MOCKS=true (dev/test only).
  const { data, error } = await mockAwareFetch<ParsedRecordsResponse>(
    `/api/patient/records${query ? `?${query}` : ""}`,
    {
      method: "GET", portal: "patient", headers: actingAsHeaders(options?.actingAsProfileId),
      // A whole history can be large; the default 30s would fail it outright.
      ...(options?.uploadId ? {} : { timeoutMs: COMBINED_RECORDS_TIMEOUT_MS }),
    }
  );

  if (error) {
    throw new Error(error.message || "Failed to fetch parsed records");
  }

  return data?.data ?? [];
}

/** Detailed upload-scoped response used only by UploadDetailPanel. Unlike the
 * shared record fetch above, this preserves full-upload source metadata and
 * supports server-side source-group filtering. */
export async function getPatientRecordsDetailed(options: {
  uploadId: string;
  sourceGroup?: SourceGroup;
  actingAsProfileId?: string;
  /** Pass true for the live-poll's own recurring re-fetch while an upload is
   *  still processing — not a patient actually opening/switching to this
   *  view. Skips the backend's HIPAA-access VIEW audit log for this call;
   *  the real "someone looked at this" event is already logged by whichever
   *  fetch is NOT a background poll (first load, tab switch, manual retry). */
  background?: boolean;
}): Promise<ParsedRecordsDetail> {
  const params = new URLSearchParams({ upload_id: options.uploadId });
  if (options.sourceGroup) params.set("source_group", options.sourceGroup);
  if (options.background) params.set("background", "true");
  const { data, error } = await apiFetch<ParsedRecordsResponse>(
    `/api/patient/records?${params.toString()}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(options.actingAsProfileId) },
  );
  if (error) throw new Error(error.message || "Failed to fetch parsed records");
  return {
    records: data?.data ?? [],
    count: data?.count ?? 0,
    hasMore: data?.hasMore ?? false,
    sourceVendors: data?.sourceVendors ?? [],
    sourceGroups: data?.sourceGroups ?? [],
    mixedSourceGroups: data?.mixedSourceGroups ?? false,
  };
}

/** Parsed MedicationRequest records only — the Medications sidebar page and the
 *  Dashboard's Current Medications card both read from this. */
export async function getMedications(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["MedicationRequest"], actingAsProfileId });
}

/** Parsed DiagnosticReport records only — the Dashboard's Recent Labs card reads
 *  from this. (The full Diagnostics & Imaging sidebar page is still mock-data-driven —
 *  its LabResult/ImagingStudy types need per-component lab values and image metadata
 *  this resource type alone can't provide; not wired up yet.) */
export async function getDiagnosticReports(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["DiagnosticReport"], actingAsProfileId });
}

/** Parsed Encounter records only — the Dashboard's Recent Visits card and its
 *  modal both read from this. (The full Care & Treatment sidebar page is still
 *  mock-data-driven — same "widget first, full page later" scope as Diagnostics.) */
export async function getEncounters(actingAsProfileId?: string): Promise<ParsedRecord[]> {
  return getPatientRecords({ resourceTypes: ["Encounter"], actingAsProfileId });
}
